import { TextField, Button, Box, Stack, Typography, Grid } from '@mui/material'
import { get_clothes_crediting_upload } from 'api/clothes_service'
import { UnauthorisedError } from 'errors'
import { ClothingDataDB } from 'models'
import React, { useContext, useState } from 'react'
import ButtonWithLoading from './button_with_loading'
import { UserContext } from 'base'
import UploadCard from './upload_card'

interface ClothingMergeProps {
  clothingData: ClothingDataDB
  onSubmit: (primaryId: string, secondaryId: string) => Promise<void>
}

const ClothingMergeForm = ({ clothingData, onSubmit }: ClothingMergeProps) => {
  enum Page {
    SECONDARY_INPUT = 1,
    CONFIRMATION = 2,
  }

  const [errorText, setErrorText] = useState<string | null>(null)
  const [secondaryData, setSecondaryData] = useState<ClothingDataDB>()
  const [page, setPage] = useState<Page>(Page.SECONDARY_INPUT)
  const [secondaryIdOrUrl, setSecondaryIdOrUrl] = useState<string>('')
  const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [previewData, setPreviewData] = useState<ClothingDataDB>(clothingData)

  // Immutable state to avoid re-rendering the primary data after merging
  const [primaryData] = useState<ClothingDataDB>(clothingData)
  const user = useContext(UserContext)

  const constructPreviewData = (secondaryData: ClothingDataDB) => {
    const cloned = Object.assign(new ClothingDataDB(), primaryData)
    cloned.total_box_count =
      cloned.total_box_count === null
        ? secondaryData.total_box_count
        : cloned.total_box_count
    cloned.processed_box_count =
      cloned.processed_box_count === null ||
      secondaryData.processed_box_count === null
        ? null
        : cloned.processed_box_count + secondaryData.processed_box_count
    cloned.clothes = [...cloned.clothes, ...secondaryData.clothes]
    cloned.rejected_items = [
      ...cloned.rejected_items,
      ...secondaryData.rejected_items,
    ]
    cloned.timestamp_created =
      secondaryData.timestamp_created < cloned.timestamp_created
        ? secondaryData.timestamp_created
        : cloned.timestamp_created
    cloned.timestamp_submitted = new Date()
    cloned.staff = [
      ...cloned.staff,
      ...secondaryData.staff.filter((staff) => !cloned.staff.includes(staff)),
    ]
    return cloned
  }

  const handleSecondaryId = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSecondaryIdOrUrl(event.target.value)
  }

  switch (page) {
    case Page.SECONDARY_INPUT:
      return (
        <Box
          component="form"
          onSubmit={async (event) => {
            setIsNextPageLoading(true)
            event.preventDefault()
            try {
              const secondaryId = secondaryIdOrUrl.split('/').pop()!
              const uploadData = await get_clothes_crediting_upload(secondaryId)
              setIsNextPageLoading(false)
              if (uploadData.id === primaryData.id) {
                setErrorText('Cannot merge an upload with itself')
                return
              }
              if (
                !uploadData.is_submitted ||
                uploadData.is_approved ||
                uploadData.is_partial_upload !== primaryData.is_partial_upload
              ) {
                setErrorText(
                  'Secondary upload status must match primary upload'
                )
                return
              }
              setSecondaryData(uploadData)
              setPreviewData(constructPreviewData(uploadData))
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                user.setLoggedOut()
              }
              setIsNextPageLoading(false)
              setErrorText('Upload not found')
              return
            }
            setErrorText(null)
            setPage(Page.CONFIRMATION)
          }}
        >
          <Stack spacing={3}>
            <Typography variant="body1">
              Please enter the ID or URL of the secondary upload. The items
              associated with the secondary upload will be added to the current
              upload. The secondary upload will be deleted.
            </Typography>
            <TextField
              required
              label="Secondary Upload ID/URL"
              fullWidth
              onChange={handleSecondaryId}
              error={errorText != null}
              helperText={errorText}
              value={secondaryIdOrUrl}
            />
            <Stack direction="row" justifyContent="right">
              <ButtonWithLoading
                type="submit"
                variant="contained"
                isLoading={isNextPageLoading}
              >
                Next
              </ButtonWithLoading>
            </Stack>
          </Stack>
        </Box>
      )
    case Page.CONFIRMATION:
      return (
        <Box
          component="form"
          onSubmit={async (event) => {
            console.assert(secondaryData !== undefined)
            setIsSaving(true)
            event.preventDefault()
            await onSubmit(primaryData.id, secondaryData!.id)
            setIsSaving(false)
          }}
        >
          <Stack spacing={3}>
            <Typography variant="body1">
              Please check the details of the primary, secondary and merged
              upload.
            </Typography>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="body1">Primary Upload:</Typography>
                <UploadCard clothingData={primaryData} />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body1">Secondary Upload:</Typography>
                <UploadCard clothingData={secondaryData!} />
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body1">Merged Upload:</Typography>
                <UploadCard clothingData={previewData} />
              </Stack>
            </Stack>

            <Grid container>
              <Grid item xs>
                <Button
                  variant="contained"
                  color="info"
                  onClick={() => {
                    setPage(Page.SECONDARY_INPUT)
                  }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs display="flex" justifyContent="right">
                <ButtonWithLoading
                  type="submit"
                  variant="contained"
                  isLoading={isSaving}
                >
                  Confirm Merge
                </ButtonWithLoading>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      )
  }
}

export default ClothingMergeForm
